import {Card, Container, createStyles, Group, Menu, rem, Space, Tabs, Text, Title, UnstyledButton} from "@mantine/core";
import {IconChevronDown, IconTruck} from "@tabler/icons-react";
import {useLocation, useNavigate} from "react-router-dom";
import {PropsWithChildren, useCallback, useMemo} from "react";
import {
  CompanySettlement,
  CompanySettlementEntity,
  getObjectId,
  SettlementEntity,
  settlementSelector,
  transportMethod,
  useGetSettlementQuery
} from "@ots-frontend/ots-data";
import {CompanySettlementDetails} from "./CompanySettlementDetails";
import {useTranslation} from "react-i18next";
import {ObjectChangeLogView} from "../common/ObjectChangeLogView/ObjectChangeLogView";
import {TabItem} from "../../tabs/Tabs/Tabs";
import {HasAccess} from "../../auth/HasAccess/HasAccess";
import {FormattedActivityPeriodDate} from "../../date/FormattedActivityPeriodDate/FormattedActivityPeriodDate";
import {
  CompanySettlementAction,
  CompanySettlementActionMenuDropdown
} from "../../menu/CompanySettlementActionMenu/CompanySettlementActionMenu";
import {skipToken} from "@reduxjs/toolkit/query";
import {CompanySettlementInsights} from "./CompanySettlementInsights";
import {
  CompanySettlementFormModal,
  CompanySettlementFormValues
} from "../../form/CompanySettlementForm/CompanySettlementForm";
import {useDisclosure} from "@mantine/hooks";
import {useCompanySettlementMutation} from "../../../hooks/useCompanySettlementMutation";
import {useApp} from "../../../providers/RegionProvider/RegionProvider";

const useStyles = createStyles((theme) => ({
  mainSection: {
    paddingBottom: theme.spacing.sm
  }
}));

export type CompanySettlementProfileProps = PropsWithChildren<{
  companySettlement: CompanySettlementEntity
}>

const tabs: TabItem[] = [
  {
    key: "details",
    value: "Details"
  },
  {
    key: "insights",
    value: "Insights"
  },
  {
    key: "history",
    value: "Change Logs",
    permissions: ["change_log_view"]
  },
];

export const CompanySettlementTitle = (props: {
  withPrefix?: boolean,
  companySettlement: CompanySettlementEntity,
  settlement?: SettlementEntity
}) => {
  const {withPrefix = true, companySettlement, settlement} = props;

  const {t} = useTranslation(["common"])

  return (
    <Title order={4}>
      {withPrefix && t("common.entity.settlement", "Settlement")}
      {(withPrefix && (settlement || companySettlement.companyName)) && ": "}
      {companySettlement.companyName && companySettlement.companyName}
      {settlement &&
        <>
          {companySettlement.companyName && " - "}
          <FormattedActivityPeriodDate
            reportTimeType={settlement.activityPeriodType}
            fromDate={settlement.activityPeriodFromDttm}
            toDate={settlement.activityPeriodToDttm}/>
        </>
      }
    </Title>
  )
}

export const CompanySettlementProfile = (props: CompanySettlementProfileProps) => {
  const {companySettlement} = props;

  const {classes} = useStyles();

  const {regionId} = useApp()
  const {t} = useTranslation()
  const navigate = useNavigate();
  const {pathname, search, state} = useLocation();

  const [formOpen, formHandler] = useDisclosure(false)

  const {
    updateCompanySettlement,
    isUpdating,
  } = useCompanySettlementMutation()

  const selectSettlement = useMemo(() => {
    return settlementSelector()
  }, []);

  const {settlement} = useGetSettlementQuery(companySettlement.settlementId ?? skipToken, {
    selectFromResult: (result) => ({
      settlement: selectSettlement(result)
    })
  });

  const onSettlementActionClick = useCallback((action: CompanySettlementAction) => {
    if (action === "TICKET_COMPARE") {
      navigate(`ticket-compare?id=${companySettlement.id}`);
    } else if (action === "EDIT") {
      formHandler.open()
    }
  }, [companySettlement, navigate, formHandler])

  const onCompanySettlementFormSubmit = useCallback((formValues: CompanySettlementFormValues) => {

    const patch: Partial<CompanySettlement> = {
      name: formValues.name,
      status: formValues.status.code,
      method: formValues.method.code,
      companyId: getObjectId(formValues.company),
    }

    updateCompanySettlement({
      ...companySettlement.asTypeValue(),
      ...patch,
    }, () => {
      formHandler.close()
    })
  }, [updateCompanySettlement, companySettlement, formHandler])

  const items = tabs.map((tab) => (
    <HasAccess accessOnEmpty={true} permissions={tab.permissions} key={tab.key}>
      <Tabs.Tab value={tab.key}>
        {tab.value}
      </Tabs.Tab>
    </HasAccess>
  ));

  return (
    <Card shadow="sm" padding="lg" mb="lg" radius="md" withBorder>
      <Container fluid={true} className={classes.mainSection}>
        <Group position="apart">
          <CompanySettlementTitle
            companySettlement={companySettlement}
            settlement={settlement}/>
          <Menu
            width={260}
            position="bottom-end"
            transitionProps={{transition: "pop-top-right"}}
            withinPortal
          >
            <Menu.Target>
              <UnstyledButton>
                <Group spacing={7}>
                  <Text weight={500} size="sm" sx={{lineHeight: 1}} mr={3}>
                    {t("common.action.plural", "Actions")}
                  </Text>
                  <IconChevronDown size={rem(12)} stroke={1.5}/>
                </Group>
              </UnstyledButton>
            </Menu.Target>
            <Menu.Dropdown>
              <CompanySettlementActionMenuDropdown
                onActionClick={onSettlementActionClick}
                settlement={companySettlement}/>
            </Menu.Dropdown>
          </Menu>
        </Group>
        {companySettlement.method === transportMethod.trucking &&
          <Group spacing="xs">
            <IconTruck size="1rem"/>
            {t(transportMethod.trucking.displayValue.id, transportMethod.trucking.displayValue.defaultMessage)}
          </Group>
        }
      </Container>

      <Card.Section>
        <Container fluid={true}>
          <Tabs
            defaultValue={state?.tabValue ?? "details"}
            onTabChange={(value) => navigate(pathname + search, {replace: true, state: {...state, tabValue: value}})}
          >
            <Tabs.List>{items}</Tabs.List>
            <Space h="md"/>
            <Tabs.Panel value="details">
              <CompanySettlementDetails settlement={companySettlement}/>
            </Tabs.Panel>
            <Tabs.Panel value="insights">
              <CompanySettlementInsights companySettlement={companySettlement}/>
            </Tabs.Panel>
            <Tabs.Panel value="history">
              <ObjectChangeLogView entity={companySettlement}/>
            </Tabs.Panel>
          </Tabs>
        </Container>
      </Card.Section>

      <CompanySettlementFormModal
        opened={formOpen}
        onClose={formHandler.close}
        onSubmit={onCompanySettlementFormSubmit}
        submitting={isUpdating}
        options={{
          regionId: companySettlement.regionId ?? regionId,
        }}
        defaultValues={{
          company: companySettlement.companyId,
          name: companySettlement.name,
          status: companySettlement.status,
          method: companySettlement.method
        }}/>
    </Card>
  );
};
